import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { tickets } from '@repletoryreach/sdk-api';
import { Button, Card, Group, Stack, Text } from '@repletoryreach/sdk-ui';
import { useT } from '../../../i18n/I18nProvider';
import { formatDateTime } from '../../../lib/format';
import { FieldInput, fieldValueText } from '../components/FieldInput';
import { TicketSlaBadge } from '../components/TicketBits';
import { channelLabel, type Lookups } from '../lib/data';
import { missingRequired, normaliseValue } from '../lib/fields';

const MUTED = 'var(--rr-color-text-muted)';

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rr-tickets__kv">
      <Text role="caption" c={MUTED}>{label}</Text>
      <div>{children}</div>
    </div>
  );
}

/** Where the ticket stands against its response targets. */
export function SlaPanel({ ticket, now }: { ticket: tickets.Ticket; now: number }) {
  const t = useT();
  const sla = ticket.sla;
  return (
    <Card withBorder>
      <Stack gap="sm">
        <Group gap="sm" justify="space-between">
          <Text role="subtitle" fw={600}>{t('tickets.sla.title')}</Text>
          <TicketSlaBadge input={ticket} now={now} size="sm" />
        </Group>
        {!sla || (!sla.first_response_due && !sla.resolution_due) ? (
          <Text role="bodySmall" c={MUTED}>{t('tickets.sla.noPolicy')}</Text>
        ) : (
          <Stack gap="xs">
            <Row label={t('tickets.sla.firstResponse')}>
              <Text role="bodySmall">
                {ticket.first_response_at
                  ? t('tickets.sla.respondedAt', { when: formatDateTime(ticket.first_response_at) })
                  : sla.first_response_due ? t('tickets.sla.dueOn', { when: formatDateTime(sla.first_response_due) }) : t('common.none')}
              </Text>
            </Row>
            <Row label={t('tickets.sla.resolution')}>
              <Text role="bodySmall">
                {ticket.solved_at
                  ? t('tickets.sla.solvedAt', { when: formatDateTime(ticket.solved_at) })
                  : sla.resolution_due ? t('tickets.sla.dueOn', { when: formatDateTime(sla.resolution_due) }) : t('common.none')}
              </Text>
            </Row>
            {sla.paused && <Text role="caption" c={MUTED}>{t('tickets.sla.pausedHint')}</Text>}
            {sla.breached && <Text role="caption" c="var(--rr-color-error)">{t('tickets.sla.breachedHint')}</Text>}
          </Stack>
        )}
      </Stack>
    </Card>
  );
}

/** The client, the channel, where it came from and the satisfaction score. */
export function DetailsPanel({ ticket, lk }: { ticket: tickets.Ticket; lk: Lookups }) {
  const t = useT();
  return (
    <Card withBorder>
      <Stack gap="sm">
        <Text role="subtitle" fw={600}>{t('tickets.detail.details')}</Text>
        <Row label={t('tickets.field.client')}>
          <Text role="bodySmall">{ticket.contact?.name || t('tickets.noClient')}</Text>
          {ticket.contact?.ref && ticket.contact.ref !== ticket.contact.name && <Text role="caption" c={MUTED}>{ticket.contact.ref}</Text>}
          {ticket.contact?.id && <Link to={`/contacts?contact=${encodeURIComponent(ticket.contact.id)}`}>{t('tickets.detail.openContact')}</Link>}
        </Row>
        <Row label={t('tickets.field.channel')}><Text role="bodySmall">{channelLabel(t, ticket.channel)}</Text></Row>
        <Row label={t('tickets.field.source')}><Text role="bodySmall">{t(`tickets.source.${ticket.source ?? 'console'}`, ticket.source ?? '')}</Text></Row>
        {ticket.team_id && <Row label={t('tickets.field.team')}><Text role="bodySmall">{lk.teamName(ticket.team_id) || t('common.unknown')}</Text></Row>}
        {ticket.closed_at && <Row label={t('tickets.detail.closedAt')}><Text role="bodySmall">{formatDateTime(ticket.closed_at)}</Text></Row>}
        {ticket.description && (
          <Row label={t('common.description')}><Text role="bodySmall" style={{ whiteSpace: 'pre-wrap' }}>{ticket.description}</Text></Row>
        )}
        <Row label={t('tickets.csat.title')}>
          {ticket.csat_score != null ? (
            <Group gap="xs" align="center">
              <span className="rr-tickets__csat" aria-hidden>
                {[1, 2, 3, 4, 5].map((n) => <span key={n} className={n <= ticket.csat_score! ? 'is-on' : ''} />)}
              </span>
              <Text role="bodySmall" fw={600}>{t('tickets.csat.score', { score: ticket.csat_score })}</Text>
            </Group>
          ) : (
            <Text role="bodySmall" c={MUTED}>{t('tickets.csat.none')}</Text>
          )}
        </Row>
      </Stack>
    </Card>
  );
}

/** The type's own fields: read, then edit and save together. Required ones are marked and checked. */
export function FieldsPanel({ ticket, lk, busy, onSave }: {
  ticket: tickets.Ticket;
  lk: Lookups;
  busy: boolean;
  onSave: (fields: Record<string, unknown>) => void;
}) {
  const t = useT();
  const type = lk.typeById.get(ticket.type.id);
  const fields = type?.fields ?? [];
  const [editing, setEditing] = useState(false);
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [tried, setTried] = useState(false);

  useEffect(() => { if (!editing) setValues({ ...(ticket.fields ?? {}) }); }, [ticket.fields, editing]);

  if (fields.length === 0) return null;
  const missing = missingRequired(fields, values);

  function save() {
    setTried(true);
    if (missing.length) return;
    const out: Record<string, unknown> = { ...(ticket.fields ?? {}) };
    for (const f of fields) out[f.key] = normaliseValue(f.type, values[f.key]);
    onSave(out);
    setEditing(false);
    setTried(false);
  }

  return (
    <Card withBorder>
      <Stack gap="sm">
        <Group gap="sm" justify="space-between">
          <Text role="subtitle" fw={600}>{t('tickets.fields.title', { type: ticket.type.name })}</Text>
          {!editing && !ticket.merged_into_id && <Button size="xs" variant="subtle" onClick={() => setEditing(true)}>{t('action.edit')}</Button>}
        </Group>
        {editing ? (
          <Stack gap="sm">
            {fields.map((f) => (
              <FieldInput
                key={f.key}
                field={f}
                value={values[f.key]}
                onChange={(v) => setValues((x) => ({ ...x, [f.key]: v }))}
                error={tried && missing.some((m) => m.key === f.key) ? t('tickets.field.requiredError') : undefined}
              />
            ))}
            <Group gap="sm" justify="flex-end">
              <Button size="xs" variant="default" onClick={() => { setEditing(false); setTried(false); }}>{t('action.cancel')}</Button>
              <Button size="xs" loading={busy} onClick={save}>{t('action.save')}</Button>
            </Group>
          </Stack>
        ) : (
          <Stack gap="xs">
            {fields.map((f) => {
              const v = fieldValueText(t, f, ticket.fields?.[f.key]);
              return (
                <Row key={f.key} label={f.required ? `${f.label} *` : f.label}>
                  <Text role="bodySmall" c={v ? undefined : MUTED} style={{ whiteSpace: 'pre-wrap' }}>
                    {v || (f.required ? t('tickets.fields.missing') : t('common.none'))}
                  </Text>
                </Row>
              );
            })}
          </Stack>
        )}
      </Stack>
    </Card>
  );
}

/** The conversations the ticket follows, each opening in the inbox. */
export function ConversationsPanel({ ticket, onLink }: { ticket: tickets.Ticket; onLink: () => void }) {
  const t = useT();
  return (
    <Card withBorder>
      <Stack gap="sm">
        <Group gap="sm" justify="space-between">
          <Text role="subtitle" fw={600}>{t('tickets.link.title')}</Text>
          {!ticket.merged_into_id && <Button size="xs" variant="subtle" onClick={onLink}>{t('tickets.link.action')}</Button>}
        </Group>
        {ticket.conversations.length === 0 ? (
          <Text role="bodySmall" c={MUTED}>{t('tickets.link.none')}</Text>
        ) : (
          <Stack gap="xs">
            {ticket.conversations.map((c, i) => (
              <Group key={c.id} gap="sm" justify="space-between" wrap="nowrap">
                <Text role="bodySmall">{t('tickets.composer.conversation', { channel: channelLabel(t, c.channel), n: i + 1 })}</Text>
                <Link to={`/inbox?conversation=${encodeURIComponent(c.id)}`}>{t('tickets.link.openInInbox')}</Link>
              </Group>
            ))}
          </Stack>
        )}
      </Stack>
    </Card>
  );
}
